import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { uiLogger, UILogEntry } from '@/lib/uiLogger';
import { AlertCircle, Download, Trash2, RefreshCw, Eye, EyeOff } from 'lucide-react';

export default function UIMonitoringDashboard() {
  const [logs, setLogs] = useState<UILogEntry[]>([]);
  const [errorLogs, setErrorLogs] = useState<UILogEntry[]>([]);
  const [performance, setPerformance] = useState<any>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedLog, setSelectedLog] = useState<UILogEntry | null>(null);

  useEffect(() => {
    const refreshData = () => {
      setLogs(uiLogger.getLogs());
      setErrorLogs(uiLogger.getErrorLogs());
      setPerformance(uiLogger.generatePerformanceReport());
    };

    refreshData();

    if (autoRefresh) {
      const interval = setInterval(refreshData, 1000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const handleExportLogs = (format: 'json' | 'csv') => {
    const content = uiLogger.exportLogs(format);
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      `data:text/${format};charset=utf-8,${encodeURIComponent(content)}`
    );
    element.setAttribute('download', `ui-logs.${format}`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleClearLogs = () => {
    if (confirm('Are you sure you want to clear all logs?')) {
      uiLogger.clearLogs();
      setLogs([]);
      setErrorLogs([]);
      setSelectedLog(null);
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'debug':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/30';
      case 'info':
        return 'bg-green-500/10 text-green-700 border-green-500/30';
      case 'warn':
        return 'bg-amber-500/10 text-amber-700 border-amber-500/30';
      case 'error':
        return 'bg-red-500/10 text-red-700 border-red-500/30';
      case 'critical':
        return 'bg-red-600/20 text-red-900 border-red-600/50';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-500/30';
    }
  };

  const getLevelEmoji = (level: string) => {
    switch (level) {
      case 'debug':
        return '🔍';
      case 'info':
        return 'ℹ️';
      case 'warn':
        return '⚠️';
      case 'error':
        return '❌';
      case 'critical':
        return '🚨';
      default:
        return '📝';
    }
  };

  return (
    <div className="space-y-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">UI Monitoring Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Real-time tracking of component rendering, data loading, and error states
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? (
              <>
                <Eye className="h-4 w-4 mr-2" />
                Live
              </>
            ) : (
              <>
                <EyeOff className="h-4 w-4 mr-2" />
                Paused
              </>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setLogs(uiLogger.getLogs())}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Performance Summary */}
      {performance && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Logs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{performance.totalLogs}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-red-600">
                Errors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {performance.errors}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-amber-600">
                Warnings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {performance.warnings}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-600">
                Avg Async Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {Math.round(performance.averageAsyncDuration)}ms
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="all-logs" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="all-logs">
            All Logs ({logs.length})
          </TabsTrigger>
          <TabsTrigger value="errors">
            Errors ({errorLogs.length})
          </TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        {/* All Logs Tab */}
        <TabsContent value="all-logs" className="space-y-4">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportLogs('json')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExportLogs('csv')}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleClearLogs}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <ScrollArea className="h-96">
                <div className="space-y-2 p-4">
                  {logs.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      No logs yet. Start interacting with the UI.
                    </div>
                  ) : (
                    logs.map((log, idx) => (
                      <div
                        key={idx}
                        onClick={() => setSelectedLog(log)}
                        className="p-3 rounded-lg border border-border/50 hover:bg-accent/50 cursor-pointer transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-lg">{getLevelEmoji(log.level)}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge variant="outline" className="text-xs">
                                {log.component}
                              </Badge>
                              <Badge
                                variant="outline"
                                className={`text-xs border ${getLevelColor(log.level)}`}
                              >
                                {log.level}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {log.action}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                          {log.duration && (
                            <span className="text-xs text-blue-600 font-mono">
                              {Math.round(log.duration)}ms
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <ScrollArea className="h-96">
                <div className="space-y-2 p-4">
                  {errorLogs.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      ✅ No errors detected!
                    </div>
                  ) : (
                    errorLogs.map((log, idx) => (
                      <div
                        key={idx}
                        onClick={() => setSelectedLog(log)}
                        className={`p-3 rounded-lg border ${getLevelColor(
                          log.level
                        )} cursor-pointer transition-colors hover:opacity-80`}
                      >
                        <div className="flex items-start gap-3">
                          <AlertCircle className="h-5 w-5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm">
                              {log.component} • {log.action}
                            </div>
                            <p className="text-xs mt-1 opacity-75">
                              {log.details?.errorMessage || log.error?.message || 'Unknown error'}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          {performance && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Slowest Operations</CardTitle>
                  <CardDescription>
                    Top 5 async operations by duration
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {performance.slowestOperations.length === 0 ? (
                      <p className="text-muted-foreground text-sm">
                        No async operations tracked yet
                      </p>
                    ) : (
                      performance.slowestOperations.map((op: any, idx: number) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg"
                        >
                          <div>
                            <p className="font-medium text-sm">{op.action}</p>
                            <p className="text-xs text-muted-foreground">
                              Operation #{idx + 1}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-lg text-blue-600">
                              {Math.round(op.duration)}
                            </div>
                            <p className="text-xs text-muted-foreground">ms</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Logs</p>
                      <p className="text-2xl font-bold">{performance.totalLogs}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Errors</p>
                      <p className="text-2xl font-bold text-red-600">
                        {performance.errors}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Warnings</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {performance.warnings}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Avg Async Duration</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {Math.round(performance.averageAsyncDuration)}ms
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Pane */}
      {selectedLog && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <span>{getLevelEmoji(selectedLog.level)}</span>
                Log Details
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedLog(null)}
              >
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Component</p>
                <p className="font-mono text-sm">{selectedLog.component}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Level</p>
                <Badge variant="outline" className={getLevelColor(selectedLog.level)}>
                  {selectedLog.level}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Action</p>
                <p className="font-mono text-sm">{selectedLog.action}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Timestamp</p>
                <p className="font-mono text-sm">
                  {new Date(selectedLog.timestamp).toLocaleString()}
                </p>
              </div>
            </div>

            {selectedLog.duration && (
              <div>
                <p className="text-sm text-muted-foreground">Duration</p>
                <p className="font-mono text-sm text-blue-600">
                  {Math.round(selectedLog.duration)}ms
                </p>
              </div>
            )}

            {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">Details</p>
                <pre className="bg-secondary/50 p-3 rounded-lg text-xs overflow-auto max-h-48">
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </div>
            )}

            {selectedLog.error && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">Error</p>
                <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-lg text-sm">
                  <p className="text-red-700 font-mono">{selectedLog.error.message}</p>
                </div>
              </div>
            )}

            {selectedLog.stackTrace && (
              <div>
                <p className="text-sm text-muted-foreground mb-2">Stack Trace</p>
                <pre className="bg-secondary/50 p-3 rounded-lg text-xs overflow-auto max-h-48 font-mono">
                  {selectedLog.stackTrace}
                </pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
